// src/pages/GatePasses/parts/ViewGatePassModal.tsx
//
// Gate pass detail view — GET /gate-passes/:id — plus every action that moves
// the pass along:
//
//   pending   → Approve (school, no OTP)   PUT  /gate-passes/:id/approve
//             → Send / verify parent OTP   POST /gate-passes/:id/otp/send | /otp/verify
//             → Reject                     PUT  /gate-passes/:id/reject
//             → Cancel                     PUT  /gate-passes/:id/cancel
//   approved  → Mark gone home / gone out (gate OUT)   POST /movements
//             → Cancel
//   out       → Mark back in school (gate IN)          POST /movements   (short leave only)
//
// The gate actions record a manual movement at the main gate; the backend
// links it to this pass and moves the status forward on its own.
//
// GET /gate-passes/:id doesn't join the student, so the name comes in as a
// prop from the list row (or from the create form for a brand-new pass).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import {
  APPROVAL_METHOD_LABEL,
  ApiGatePass,
  GatePassRecord,
  OVERDUE_META,
  PASS_TYPE_META,
  RELATION_ALONE,
  STATUS_META,
  formatPassDate,
  formatTime,
  getInitials,
  isOverdue,
  mapApiGatePass,
  statusLabel,
} from './types';

interface ViewGatePassModalProps {
  open: boolean;
  passId: string | null;
  studentName: string;
  spStudentId: string;
  onClose: () => void;
  /** Called after any successful action so the list can refresh. */
  onChanged: () => void;
}

interface MovementInfo {
  timestamp: string;
  gate: string;
  method: string;
}

type ActionKey =
  | 'approve'
  | 'reject'
  | 'cancel'
  | 'otp-send'
  | 'otp-verify'
  | 'mark-out'
  | 'mark-in';

const OTP_MAX_ATTEMPTS = 3; // gateOTPMaxAttempts in gateController.go

// ── Presentational pieces ───────────────────────────────────────────────────

const sectionCardStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.8)',
  borderRadius: 16,
  padding: 20,
  border: '1px solid rgba(59, 130, 246, 0.1)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
};

const Row: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 12,
      background: 'rgba(248, 250, 252, 0.9)',
      border: '1px solid rgba(226, 232, 240, 0.8)',
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.12) 100%)',
        color: '#3b82f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <i className={icon} style={{ fontSize: 14 }} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
        {value || <span style={{ color: '#94a3b8', fontWeight: 500 }}>—</span>}
      </div>
    </div>
  </div>
);

const Section: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div style={sectionCardStyle}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 14 }} />
      </div>
      <span
        style={{
          color: '#1e3a8a',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </span>
    </div>
    {children}
  </div>
);

const Badge: React.FC<{ meta: { label: string; color: string; bg: string; border: string; icon: string } }> = ({
  meta,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '7px 14px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: meta.color,
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      flexShrink: 0,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}
  >
    <i className={meta.icon} style={{ fontSize: 12 }} />
    {meta.label}
  </span>
);

const movementLabel = (m: MovementInfo | null): string =>
  m ? `${formatTime(m.timestamp)} · ${m.gate} gate (${m.method})` : '';

// ── Progress tracker ────────────────────────────────────────────────────────
// Early departure: Requested → Approved → Gone home
// Short leave:     Created → Went out → Back in school (approved on creation)
// Rejected / cancelled passes stop at the last step they reached and end in
// a red / grey "stopped" step.

interface TrackerStep {
  label: string;
  time: string | null;
  icon: string;
}

const PassTracker: React.FC<{
  pass: GatePassRecord;
  outMovement: MovementInfo | null;
  inMovement: MovementInfo | null;
  overdue: boolean;
}> = ({ pass, outMovement, inMovement, overdue }) => {
  const meta = PASS_TYPE_META[pass.passType];
  const isShort = pass.passType === 'short_leave';

  const steps: TrackerStep[] = isShort
    ? [
        { label: 'Created', time: pass.createdAt, icon: 'pi pi-plus' },
        { label: 'Went out', time: outMovement?.timestamp ?? null, icon: 'pi pi-sign-out' },
        { label: 'Back in school', time: inMovement?.timestamp ?? null, icon: 'pi pi-sign-in' },
      ]
    : [
        { label: 'Requested', time: pass.createdAt, icon: 'pi pi-plus' },
        { label: 'Approved', time: pass.approvedAt, icon: 'pi pi-check' },
        { label: 'Gone home', time: outMovement?.timestamp ?? null, icon: 'pi pi-home' },
      ];

  const reachedByStatus: Record<string, number> = isShort
    ? { pending: 0, approved: 0, out: 1, returned: 2 }
    : { pending: 0, approved: 1, completed: 2 };

  const stopped = pass.status === 'rejected' || pass.status === 'cancelled';
  const reached = stopped ? (pass.approvedAt && !isShort ? 1 : 0) : reachedByStatus[pass.status] ?? 0;
  const shown: (TrackerStep & { state: 'done' | 'next' | 'todo' | 'stopped' | 'late' })[] = [];

  steps.forEach((s, i) => {
    if (stopped && i > reached) return;
    let state: 'done' | 'next' | 'todo' | 'late' = i <= reached ? 'done' : i === reached + 1 ? 'next' : 'todo';
    if (state === 'next' && overdue && s.label === 'Back in school') state = 'late';
    shown.push({ ...s, state });
  });
  if (stopped) {
    shown.push({
      label: pass.status === 'rejected' ? 'Rejected' : 'Cancelled',
      time: pass.status === 'rejected' ? pass.rejectedAt : pass.cancelledAt,
      icon: pass.status === 'rejected' ? 'pi pi-times' : 'pi pi-ban',
      state: 'stopped',
    });
  }

  const colorFor = (state: string) =>
    state === 'done'
      ? meta.color
      : state === 'late'
        ? '#dc2626'
        : state === 'stopped'
          ? pass.status === 'rejected'
            ? '#dc2626'
            : '#6b7280'
          : '#cbd5e1';

  return (
    <div
      style={{
        ...sectionCardStyle,
        border: `1px solid ${meta.border}`,
        background: `linear-gradient(180deg, ${meta.bg} 0%, rgba(255,255,255,.9) 100%)`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {shown.map((s, i) => {
          const c = colorFor(s.state);
          const filled = s.state === 'done' || s.state === 'stopped';
          const nextState = shown[i + 1]?.state;
          const lineDone = nextState === 'done' || nextState === 'stopped';
          return (
            <React.Fragment key={s.label}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 76, flex: '0 0 auto' }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: filled ? c : 'white',
                    color: filled ? 'white' : c,
                    border: `2px ${s.state === 'next' || s.state === 'late' ? 'dashed' : 'solid'} ${c}`,
                    fontSize: 13,
                  }}
                >
                  <i className={s.state === 'late' ? 'pi pi-exclamation-triangle' : s.icon} />
                </div>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    color: s.state === 'todo' ? '#94a3b8' : s.state === 'next' ? '#475569' : c,
                    textAlign: 'center',
                  }}
                >
                  {s.state === 'late' ? 'Overdue' : s.label}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 2, minHeight: 14 }}>
                  {s.time ? formatTime(s.time) : s.state === 'next' ? 'next' : ''}
                </div>
              </div>
              {i < shown.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    marginTop: 17,
                    background: lineDone ? meta.color : '#e2e8f0',
                    borderRadius: 2,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ── Component ───────────────────────────────────────────────────────────────

const ViewGatePassModal: React.FC<ViewGatePassModalProps> = ({
  open,
  passId,
  studentName,
  spStudentId,
  onClose,
  onChanged,
}) => {
  const toast = useRef<Toast>(null);
  const [pass, setPass] = useState<GatePassRecord | null>(null);
  const [outMovement, setOutMovement] = useState<MovementInfo | null>(null);
  const [inMovement, setInMovement] = useState<MovementInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ActionKey | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [now, setNow] = useState(() => new Date());

  // Keeps the OTP expiry and overdue badge honest while the modal sits open.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(t);
  }, [open]);

  const loadMovements = async (p: GatePassRecord) => {
    if (!p.outMovementId && !p.inMovementId) {
      setOutMovement(null);
      setInMovement(null);
      return;
    }
    try {
      const res = await apiClient.get('api/movements', {
        params: { student_id: p.studentId, date: p.date, limit: 100 },
      });
      if (res.data.status !== 'success') return;
      const rows: any[] = res.data.data || [];
      const find = (id: string | null): MovementInfo | null => {
        if (!id) return null;
        const m = rows.find((r) => (r._id || r.id) === id);
        return m ? { timestamp: m.timestamp, gate: m.gate, method: m.method } : null;
      };
      setOutMovement(find(p.outMovementId));
      setInMovement(find(p.inMovementId));
    } catch {
      // Times are a nice-to-have; the pass itself already loaded.
    }
  };

  const fetchPass = useCallback(
    async (showSpinner: boolean) => {
      if (!passId) return;
      if (showSpinner) setLoading(true);
      try {
        const res = await apiClient.get(`api/gate-passes/${passId}`);
        if (res.data.status === 'success') {
          const mapped = mapApiGatePass(res.data.data as ApiGatePass);
          mapped.studentName = mapped.studentName || studentName;
          mapped.spStudentId = mapped.spStudentId || spStudentId;
          setPass(mapped);
          loadMovements(mapped);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Not loaded',
            detail: res.data.message || 'This gate pass could not be loaded.',
            life: 5000,
          });
        }
      } catch (err: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Could not load this gate pass.',
          life: 5000,
        });
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [passId, studentName, spStudentId],
  );

  useEffect(() => {
    if (!open || !passId) {
      setPass(null);
      setOutMovement(null);
      setInMovement(null);
      setIsClosing(false);
      return;
    }
    setOtpCode('');
    setShowCancel(false);
    setCancelReason('');
    fetchPass(true);
  }, [open, passId, fetchPass]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (busy) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  // Shared runner: every action is "call, toast, refetch, tell the list".
  const runAction = async (
    key: ActionKey,
    request: () => Promise<any>,
    success: { summary: string; detail: string },
    onOk?: (data: any) => boolean | void,
  ) => {
    if (busy) return;
    setBusy(key);
    try {
      const res = await request();
      if (res.data.status === 'success') {
        if (onOk && onOk(res.data) === false) return;
        toast.current?.show({ severity: 'success', life: 4000, ...success });
        await fetchPass(false);
        onChanged();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'That action could not be completed.',
          life: 5000,
        });
        // A failed OTP attempt changes attempts_left; a stale-status error
        // means someone else moved the pass. Either way, show fresh data.
        await fetchPass(false);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong.',
        life: 5000,
      });
    } finally {
      setBusy(null);
    }
  };

  const name = pass?.studentName || studentName || 'Student';

  const approve = () =>
    pass &&
    runAction('approve', () => apiClient.put(`api/gate-passes/${pass.id}/approve`), {
      summary: 'Approved',
      detail: `${name} can now leave through the gate.`,
    });

  const reject = () => {
    if (!pass) return;
    if (!window.confirm(`Reject the gate pass for ${name}?`)) return;
    runAction('reject', () => apiClient.put(`api/gate-passes/${pass.id}/reject`), {
      summary: 'Rejected',
      detail: 'The gate pass was rejected.',
    });
  };

  const cancel = () =>
    pass &&
    runAction(
      'cancel',
      () => apiClient.put(`api/gate-passes/${pass.id}/cancel`, { reason: cancelReason.trim() }),
      { summary: 'Cancelled', detail: 'The gate pass was cancelled.' },
      () => {
        setShowCancel(false);
        setCancelReason('');
      },
    );

  const sendOtp = () =>
    pass &&
    runAction(
      'otp-send',
      () => apiClient.post(`api/gate-passes/${pass.id}/otp/send`, { channel: 'App' }),
      { summary: 'OTP sent', detail: 'Ask the parent for the 6-digit code.' },
      () => setOtpCode(''),
    );

  const verifyOtp = () => {
    if (!pass || otpCode.length !== 6) return;
    runAction(
      'otp-verify',
      () => apiClient.post(`api/gate-passes/${pass.id}/otp/verify`, { otp_code: otpCode }),
      { summary: 'Verified', detail: `Parent approved. ${name} can now leave.` },
      () => setOtpCode(''),
    );
  };

  const recordMovement = (direction: 'OUT' | 'IN') => {
    if (!pass) return;
    const expectedType = direction === 'IN' ? 'return' : pass.passType;
    runAction(
      direction === 'OUT' ? 'mark-out' : 'mark-in',
      () =>
        apiClient.post('api/movements', {
          student_id: pass.studentId,
          direction,
          gate: 'main',
          method: 'manual',
        }),
      direction === 'OUT'
        ? {
            summary: pass.passType === 'short_leave' ? 'Gone out' : 'Gone home',
            detail:
              pass.passType === 'short_leave'
                ? `${name} is out. Mark the return when they come back.`
                : `${name} has left for the day.`,
          }
        : { summary: 'Back in school', detail: `${name} is back in school.` },
      (data) => {
        // The backend picks the movement type itself. If it didn't link to
        // this pass (e.g. the pass changed in the meantime), say so.
        if (data.type !== expectedType) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Recorded, but not on this pass',
            detail: `The gate entry was saved as "${data.type}". Refresh and check the pass status.`,
            life: 6000,
          });
          fetchPass(false);
          onChanged();
          return false;
        }
      },
    );
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const overdue = pass ? isOverdue(pass, now) : false;
  const wentAlone = pass?.relation === RELATION_ALONE;
  const backBy = pass?.expectedReturnAt ? formatTime(pass.expectedReturnAt) : '';
  const gateHeadline = !pass
    ? ''
    : pass.status === 'approved'
      ? pass.passType === 'short_leave'
        ? backBy
          ? `Ready to go out · back by ${backBy}`
          : 'Ready to go out'
        : 'Ready to go home'
      : overdue
        ? `Overdue · was due back by ${backBy}`
        : backBy
          ? `Out of school · back by ${backBy}`
          : 'Out of school · no return time set';
  const statusMeta = pass ? (overdue ? OVERDUE_META : STATUS_META[pass.status]) : null;

  const otp = pass?.otp ?? null;
  const otpExpired = !!otp && new Date(otp.expiresAt).getTime() <= now.getTime();
  const otpLocked = !!otp && otp.attempts >= OTP_MAX_ATTEMPTS;
  const otpUsable = !!otp && !otp.verified && !otpExpired && !otpLocked;

  const canCancel = pass?.status === 'pending' || pass?.status === 'approved';
  const typeMeta = pass ? PASS_TYPE_META[pass.passType] : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0', width: '100%' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: typeMeta && !loading ? typeMeta.gradient : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          fontSize: 18,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 8px 24px ${typeMeta && !loading ? typeMeta.border : 'rgba(59, 130, 246, 0.3)'}`,
          border: '3px solid white',
        }}
      >
        {loading ? <i className="pi pi-ticket" style={{ fontSize: 20 }} /> : getInitials(name)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', lineHeight: 1.2, marginBottom: 6 }}>
          {loading ? 'Loading gate pass…' : name}
        </div>
        {loading || !typeMeta ? (
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>Fetching pass details</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                color: typeMeta.color,
                background: typeMeta.bg,
                border: `1px solid ${typeMeta.border}`,
              }}
            >
              <i className={typeMeta.icon} style={{ fontSize: 11 }} />
              {typeMeta.label}
            </span>
            {pass?.spStudentId && (
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, fontFamily: 'monospace' }}>
                {pass.spStudentId}
              </span>
            )}
          </div>
        )}
      </div>
      {statusMeta && pass && !loading && (
        <Badge meta={overdue ? statusMeta : { ...statusMeta, label: statusLabel(pass.status, pass.passType) }} />
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 140px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!busy}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important; }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
          .gatepass-otp-input { width: 168px; padding: 10px 14px; border-radius: 10px; border: 1.5px solid #e5e7eb; font-size: 20px; font-weight: 700; letter-spacing: 8px; text-align: center; font-family: monospace; color: #1e293b; outline: none; background: white; }
          .gatepass-otp-input:focus { border-color: rgba(59,130,246,.55); }
          .gatepass-approve-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
          @media (max-width: 640px) { .gatepass-approve-grid { grid-template-columns: 1fr; } }
        `}</style>

        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '64px 20px',
              gap: 16,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              }}
            >
              <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" animationDuration=".8s" />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#1e40af', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Loading gate pass</div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>Fetching pass details…</div>
            </div>
          </div>
        ) : pass ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ── Where this pass is up to ── */}
            <PassTracker pass={pass} outMovement={outMovement} inMovement={inMovement} overdue={overdue} />

            {/* ── Approval (pending only) ── */}
            {pass.status === 'pending' && (
              <Section icon="pi pi-shield" title="Approve this pass">
                <div className="gatepass-approve-grid">
                  {/* School approval */}
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      border: '1px solid rgba(59,130,246,.2)',
                      background: 'linear-gradient(135deg, rgba(239,246,255,.9) 0%, rgba(255,255,255,.9) 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a' }}>
                      <i className="pi pi-building" style={{ marginRight: 8 }} />
                      School approves
                    </div>
                    <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5, flex: 1 }}>
                      No OTP needed. Use this when the principal or admin has already spoken to the parent.
                    </div>
                    <div>
                      <GlobalButton
                        label={busy === 'approve' ? 'Approving…' : 'Approve now'}
                        icon="pi pi-check"
                        onClick={approve}
                        variant="primary"
                        size="medium"
                        disabled={!!busy}
                        loading={busy === 'approve'}
                        fullWidth={false}
                      />
                    </div>
                  </div>

                  {/* Parent OTP approval */}
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      border: '1px solid rgba(124,58,237,.18)',
                      background: 'linear-gradient(135deg, rgba(245,243,255,.9) 0%, rgba(255,255,255,.9) 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#5b21b6' }}>
                      <i className="pi pi-mobile" style={{ marginRight: 8 }} />
                      Parent approves with OTP
                    </div>

                    {otpUsable ? (
                      <>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
                          Code sent via {otp!.channel}. Expires at {formatTime(otp!.expiresAt)} ·{' '}
                          {OTP_MAX_ATTEMPTS - otp!.attempts} tries left.
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            className="gatepass-otp-input"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="••••••"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            onKeyDown={(e) => e.key === 'Enter' && verifyOtp()}
                            disabled={!!busy}
                          />
                          <GlobalButton
                            label={busy === 'otp-verify' ? 'Verifying…' : 'Verify'}
                            icon="pi pi-check-circle"
                            onClick={verifyOtp}
                            variant="primary"
                            size="medium"
                            disabled={!!busy || otpCode.length !== 6}
                            loading={busy === 'otp-verify'}
                            fullWidth={false}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={sendOtp}
                          disabled={!!busy}
                          style={{
                            alignSelf: 'flex-start',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#7c3aed',
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: busy ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <i className="pi pi-refresh" style={{ fontSize: 11, marginRight: 5 }} />
                          Resend code
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5, flex: 1 }}>
                          {otpLocked
                            ? 'Too many wrong codes. Send a new one to try again.'
                            : otpExpired
                              ? 'The last code expired. Send a new one.'
                              : 'Sends a 6-digit code to the parent. They read it out at the gate. Valid for 2 minutes.'}
                        </div>
                        <div>
                          <GlobalButton
                            label={busy === 'otp-send' ? 'Sending…' : otp ? 'Send new code' : 'Send OTP to parent'}
                            icon="pi pi-send"
                            onClick={sendOtp}
                            variant="secondary"
                            size="medium"
                            disabled={!!busy}
                            loading={busy === 'otp-send'}
                            fullWidth={false}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Section>
            )}

            {/* ── Gate action ── */}
            {(pass.status === 'approved' || (pass.status === 'out' && pass.passType === 'short_leave')) && (
              <div
                style={{
                  ...sectionCardStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                  border: overdue ? '1px solid rgba(239,68,68,.35)' : sectionCardStyle.border,
                  background: overdue ? 'rgba(254,242,242,.9)' : sectionCardStyle.background,
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: overdue ? '#b91c1c' : '#1e3a8a', marginBottom: 4 }}>
                    {gateHeadline}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
                    {pass.status === 'approved'
                      ? pass.passType === 'short_leave'
                        ? 'Tap when the student steps out of the gate.'
                        : 'Tap when the student leaves with the pickup person.'
                      : 'Tap when the student is back inside. This closes the pass.'}
                  </div>
                </div>
                {pass.status === 'approved' ? (
                  <GlobalButton
                    label={
                      busy === 'mark-out'
                        ? 'Recording…'
                        : pass.passType === 'short_leave'
                          ? 'Mark as gone out'
                          : 'Mark as gone home'
                    }
                    icon="pi pi-sign-out"
                    onClick={() => recordMovement('OUT')}
                    variant="primary"
                    size="medium"
                    disabled={!!busy}
                    loading={busy === 'mark-out'}
                    fullWidth={false}
                  />
                ) : (
                  <GlobalButton
                    label={busy === 'mark-in' ? 'Recording…' : 'Mark back in school'}
                    icon="pi pi-sign-in"
                    onClick={() => recordMovement('IN')}
                    variant="primary"
                    size="medium"
                    disabled={!!busy}
                    loading={busy === 'mark-in'}
                    fullWidth={false}
                  />
                )}
              </div>
            )}

            {/* ── Timeline ── */}
            <Section icon="pi pi-clock" title="Times & gate details">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <Row icon="pi pi-calendar" label="Date" value={formatPassDate(pass.date)} />
                <Row
                  icon="pi pi-plus-circle"
                  label={pass.passType === 'short_leave' ? 'Created' : 'Requested'}
                  value={formatTime(pass.createdAt)}
                />
                {pass.passType !== 'short_leave' && (
                  <Row
                    icon="pi pi-check-circle"
                    label="Approved"
                    value={
                      pass.approvedAt
                        ? `${formatTime(pass.approvedAt)}${pass.approvalMethod ? ` · ${APPROVAL_METHOD_LABEL[pass.approvalMethod]}` : ''}`
                        : ''
                    }
                  />
                )}
                {pass.passType === 'short_leave' ? (
                  <>
                    <Row icon="pi pi-sign-out" label="Went out" value={movementLabel(outMovement)} />
                    <Row
                      icon="pi pi-hourglass"
                      label="Back by"
                      value={pass.expectedReturnAt ? formatTime(pass.expectedReturnAt) : 'Not set'}
                    />
                    <Row icon="pi pi-sign-in" label="Back in school" value={movementLabel(inMovement)} />
                  </>
                ) : (
                  <Row icon="pi pi-home" label="Gone home" value={movementLabel(outMovement)} />
                )}
                {pass.status === 'rejected' && (
                  <Row icon="pi pi-times-circle" label="Rejected" value={formatTime(pass.rejectedAt)} />
                )}
                {pass.status === 'cancelled' && (
                  <Row icon="pi pi-ban" label="Cancelled" value={formatTime(pass.cancelledAt)} />
                )}
              </div>
              {pass.status === 'cancelled' && pass.cancelReason && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(248, 250, 252, 0.9)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    fontSize: 14,
                    color: '#475569',
                    fontWeight: 500,
                  }}
                >
                  <span style={{ fontWeight: 700 }}>Cancel reason: </span>
                  {pass.cancelReason}
                </div>
              )}
            </Section>

            {/* ── Reason ── */}
            <Section icon="pi pi-bookmark" title="Reason">
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(248, 250, 252, 0.9)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  fontSize: 14,
                  color: '#334155',
                  lineHeight: 1.55,
                  fontWeight: 500,
                }}
              >
                {pass.reason}
              </div>
            </Section>

            {/* ── Pickup ── */}
            <Section icon="pi pi-users" title={pass.passType === 'short_leave' ? 'Goes out with' : 'Picked up by'}>
              {wentAlone ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(248, 250, 252, 0.9)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    fontSize: 14,
                    color: '#334155',
                    fontWeight: 600,
                  }}
                >
                  <i className="pi pi-user" style={{ color: '#0f766e' }} />
                  Going alone — no one is accompanying the student
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                  <Row icon="pi pi-user" label="Name" value={pass.pickedUpBy} />
                  <Row icon="pi pi-heart" label="Relation" value={pass.relation} />
                  <Row icon="pi pi-phone" label="Contact" value={pass.contactNo} />
                </div>
              )}
            </Section>

            {/* ── Cancel ── */}
            {canCancel && showCancel && (
              <div style={{ ...sectionCardStyle, border: '1px solid rgba(239,68,68,.25)' }}>
                <GlobalTextArea
                  label="Why is this pass being cancelled? (optional)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Parent called back, student is staying"
                  rows={2}
                  autoResize
                  disabled={!!busy}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <GlobalButton
                    label={busy === 'cancel' ? 'Cancelling…' : 'Confirm cancel'}
                    icon="pi pi-ban"
                    onClick={cancel}
                    variant="primary"
                    size="medium"
                    disabled={!!busy}
                    loading={busy === 'cancel'}
                    fullWidth={false}
                  />
                  <GlobalButton
                    label="Keep pass"
                    onClick={() => setShowCancel(false)}
                    variant="secondary"
                    size="medium"
                    disabled={!!busy}
                    fullWidth={false}
                  />
                </div>
              </div>
            )}

            {/* ── Footer actions ── */}
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
              {pass.status === 'pending' && (
                <GlobalButton
                  label={busy === 'reject' ? 'Rejecting…' : 'Reject'}
                  icon="pi pi-times"
                  onClick={reject}
                  variant="secondary"
                  size="medium"
                  disabled={!!busy}
                  loading={busy === 'reject'}
                  fullWidth={false}
                />
              )}
              {canCancel && !showCancel && (
                <GlobalButton
                  label="Cancel pass"
                  icon="pi pi-ban"
                  onClick={() => setShowCancel(true)}
                  variant="secondary"
                  size="medium"
                  disabled={!!busy}
                  fullWidth={false}
                />
              )}
              <GlobalButton
                label="Close"
                onClick={handleClose}
                variant="secondary"
                size="medium"
                disabled={!!busy}
                fullWidth={false}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '56px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.25)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ fontSize: 22, color: 'white' }} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Gate pass unavailable</div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, maxWidth: 280 }}>
              This gate pass could not be loaded.
            </div>
            <div style={{ marginTop: 8 }}>
              <GlobalButton label="Close" onClick={handleClose} variant="secondary" size="medium" fullWidth={false} />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ViewGatePassModal;